import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import apiClient, { API_BASE_URL } from '../utils/apiClient';
import Navbar from '../components/Navbar';
import './FeedbackThread.css';

const FeedbackThread = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const threadId = searchParams.get('thread');
  const lang = searchParams.get('lang') || 'zh'; // Default to Chinese
  
  const [thread, setThread] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [commentImages, setCommentImages] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [gameDetails, setGameDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userVotes, setUserVotes] = useState({ threadVote: null, commentVotes: {} });
  const [votingInProgress, setVotingInProgress] = useState({});

  const translations = {
    zh: {
      feedbackTypes: {
        game_issue: '游戏问题',
        info_error: '信息错误',
        manual_request: '手册请求',
        new_game: '新游戏请求',
        recondb_entry: 'ReconDB提交',
        site_bug: '网站Bug',
        system_notice: '系统公告'
      },
      statusTypes: {
        open: '待处理',
        replied: '已回复',
        completed: '已完成',
        delayed: '进行中',
        rejected: '暂不处理'
      },
      backToList: '← 返回列表',
      copyLink: '复制链接',
      linkCopied: '链接已复制到剪贴板',
      copyFailed: '复制失败，请手动复制链接',
      relatedGame: '关联游戏',
      attachment: '附件',
      postedOn: '发布于',
      replies: '回复',
      noReplies: '暂无回复',
      edited: '已编辑',
      addReply: '添加回复',
      replyPlaceholder: '输入您的回复...',
      attachmentOptional: '图片 (可选)',
      addImage: '添加图片',
      imageHint: '最多上传3张图片，支持JPG、PNG、GIF等格式，每张最大5MB',
      submitting: '提交中...',
      submitReply: '提交回复',
      enterComment: '请输入评论内容',
      commentFailed: '评论提交失败，请稍后重试',
      loadFailed: '无法加载反馈详情',
      noThreadId: '未指定反馈ID',
      gameTitle: '游戏标题',
      platform: '平台',
      author: '作者',
      link: '链接',
      upvote: '赞',
      downvote: '踩'
    },
    en: {
      feedbackTypes: {
        game_issue: 'Game Issue',
        info_error: 'Information Error',
        manual_request: 'Manual Request',
        new_game: 'New Game Request',
        recondb_entry: 'ReconDB Submission',
        site_bug: 'Site Bug',
        system_notice: 'System Announcement'
      },
      statusTypes: {
        open: 'Pending',
        replied: 'Replied',
        completed: 'Completed',
        delayed: 'In Progress',
        rejected: "Won't Fix"
      },
      backToList: '← Back to List',
      copyLink: 'Copy Link',
      linkCopied: 'Link copied to clipboard',
      copyFailed: 'Copy failed, please copy manually',
      relatedGame: 'Related Game',
      attachment: 'Attachment',
      postedOn: 'Posted on',
      replies: 'Replies',
      noReplies: 'No replies yet',
      edited: 'Edited',
      addReply: 'Add Reply',
      replyPlaceholder: 'Enter your reply...',
      attachmentOptional: 'Images (Optional)',
      addImage: 'Add Image',
      imageHint: 'Up to 3 images, supports JPG, PNG, GIF, max 5MB each',
      submitting: 'Submitting...',
      submitReply: 'Submit Reply',
      enterComment: 'Please enter comment content',
      commentFailed: 'Failed to submit comment, please try again later',
      loadFailed: 'Unable to load feedback details',
      noThreadId: 'No thread ID specified',
      gameTitle: 'Game Title',
      platform: 'Platform',
      author: 'Author',
      link: 'Link',
      upvote: 'Upvote',
      downvote: 'Downvote'
    }
  };

  const t = translations[lang] || translations.zh;
  const feedbackTypes = t.feedbackTypes;
  const statusTypes = t.statusTypes;

  // eslint-disable-next-line no-unused-vars
  const statusColors = {
    open: '#e4cb86',
    replied: '#2196f3',
    completed: '#4caf50',
    delayed: '#ff9800',
    rejected: '#f44336'
  };

  const fetchUserVotes = useCallback(async () => {
    try {
      const response = await apiClient.get(`/feedback/threads/${threadId}/user-votes`);
      setUserVotes(response.data);
    } catch (error) {
      console.error('Error fetching user votes:', error);
    }
  }, [threadId]);

  const fetchThread = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiClient.get(`/feedback/threads/${threadId}`);
      setThread(response.data.thread);
      setComments(response.data.comments);
      
      // Fetch game details if game_id exists
      if (response.data.thread.game_id) {
        try {
          const gameResponse = await apiClient.get(`/games/${response.data.thread.game_id}`);
          setGameDetails(gameResponse.data);
        } catch (gameError) {
          console.error('Error fetching game details:', gameError);
        }
      }
    } catch (error) {
      console.error('Error fetching thread:', error);
      alert(t.loadFailed);
      navigate('/feedback');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId]); // navigate and t.loadFailed are stable and don't need to be in dependencies

  useEffect(() => {
    if (threadId) {
      fetchThread();
      fetchUserVotes();
    }
  }, [threadId, fetchThread, fetchUserVotes]);

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    
    if (!newComment.trim()) {
      alert(t.enterComment);
      return;
    }

    setSubmitting(true);
    
    const formData = new FormData();
    formData.append('content', newComment);
    formData.append('is_developer', 0);
    
    // Append multiple images
    commentImages.forEach((image, index) => {
      formData.append(`image_${index}`, image);
    });

    try {
      await apiClient.post(`/feedback/threads/${threadId}/comments`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Reset form
      setNewComment('');
      setCommentImages([]);
      
      // Refresh thread
      fetchThread();
    } catch (error) {
      console.error('Error submitting comment:', error);
      alert(t.commentFailed);
    } finally {
      setSubmitting(false);
    }
  };

  const handleVote = async (type, voteType, id) => {
    const key = `${type}-${id}`;
    if (votingInProgress[key]) return;

    setVotingInProgress(prev => ({ ...prev, [key]: true }));

    try {
      const endpoint = type === 'thread' 
        ? `/feedback/threads/${id}/vote`
        : `/feedback/comments/${id}/vote`;
      
      const response = await apiClient.post(endpoint, { vote_type: voteType });

      if (type === 'thread') {
        setThread(prev => ({
          ...prev,
          upvotes: response.data.upvotes,
          downvotes: response.data.downvotes
        }));
        setUserVotes(prev => ({
          ...prev,
          threadVote: response.data.userVote
        }));
      } else {
        setComments(prev => prev.map(comment => 
          comment.id === id 
            ? { ...comment, upvotes: response.data.upvotes, downvotes: response.data.downvotes }
            : comment
        ));
        setUserVotes(prev => ({
          ...prev,
          commentVotes: {
            ...prev.commentVotes,
            [id]: response.data.userVote
          }
        }));
      }
    } catch (error) {
      console.error('Error voting:', error);
    } finally {
      setVotingInProgress(prev => ({ ...prev, [key]: false }));
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString(lang === 'zh' ? 'zh-CN' : 'en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Parse attachment field - can be JSON array or single string
  const parseAttachments = (attachment) => {
    if (!attachment) return [];
    
    try {
      // Try to parse as JSON array
      const parsed = JSON.parse(attachment);
      return Array.isArray(parsed) ? parsed : [attachment];
    } catch (e) {
      // If parsing fails, treat as single attachment
      return [attachment];
    }
  };

  // Render attachment based on file type
  const renderAttachment = (attachmentPath, index) => {
    const isImage = /\.(jpg|jpeg|png|gif|bmp|webp|svg)$/i.test(attachmentPath);
    
    if (isImage) {
      return (
        <img 
          key={index}
          src={`${API_BASE_URL}${attachmentPath}`}
          alt={`Attachment ${index + 1}`}
          className="attachment-image"
          onClick={() => window.open(`${API_BASE_URL}${attachmentPath}`, '_blank')}
        />
      );
    } else {
      return (
        <a 
          key={index}
          href={`${API_BASE_URL}${attachmentPath}`}
          target="_blank"
          rel="noopener noreferrer"
          className="attachment-link"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
          </svg>
          {attachmentPath.split('/').pop()}
        </a>
      );
    }
  };

  const copyThreadLink = () => {
    const link = `${window.location.origin}/feedback?thread=${threadId}&lang=${lang}`;
    navigator.clipboard.writeText(link).then(() => {
      alert(t.linkCopied);
    }).catch(() => {
      alert(t.copyFailed);
    });
  };

  if (!threadId) {
    return (
      <div className="feedback-thread-page">
        <Navbar />
        <div className="thread-container">
          <div className="error-message">{t.noThreadId}</div>
          <button className="feedback-back-button" onClick={() => navigate('/feedback')}>
            {t.backToList}
          </button>
        </div>
      </div>
    );
  }

  if (loading || !thread) {
    return (
      <div className="feedback-thread-page">
        <Navbar />
        <div className="thread-container">
          <div className="thread-header-buttons">
            <button className="feedback-back-button" onClick={() => navigate('/feedback')}>
              {t.backToList}
            </button>
            <button className="copy-link-button" onClick={copyThreadLink}>
              {t.copyLink}
            </button>
          </div>
          <div className="thread-loading-spinner-container">
            <div className="thread-loading-spinner"></div>
            <div className="thread-loading-text">加载中...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="feedback-thread-page">
      <Navbar />
      
      <div className="thread-container">
        <div className="thread-header-buttons">
          <button className="feedback-back-button" onClick={() => navigate('/feedback')}>
            {t.backToList}
          </button>
          <button className="copy-link-button" onClick={copyThreadLink}>
            {t.copyLink}
          </button>
        </div>

        <div className={`thread-detail ${thread.is_pinned === 1 ? 'pinned' : ''}`}>
          <div className="thread-meta">
            <div className="meta-left">
              <span className="thread-type-badge">{feedbackTypes[thread.type]}</span>
              <div className={`thread-status-flag status-${thread.status}`}>
                <span className="status-text">{statusTypes[thread.status]}</span>
              </div>
            </div>
          </div>

          <h1 className="thread-title">{thread.title}</h1>

          {(gameDetails || thread.game_name) && thread.type !== 'recondb_entry' && (
            <div className="thread-game-info">
              {t.relatedGame}: <span className="game-name">
                {gameDetails && gameDetails.titlechn && gameDetails.titleeng
                  ? `${gameDetails.titlechn} (${gameDetails.titleeng})`
                  : gameDetails
                  ? (gameDetails.titlechn || gameDetails.titleeng)
                  : thread.game_name}
              </span>
            </div>
          )}

          <div className="thread-content">
            <div className="content-text">{thread.content}</div>
            
            {thread.attachment && parseAttachments(thread.attachment).length > 0 && (
              <div className="attachment-section">
                <div className="attachment-label">{t.attachment}:</div>
                <div className="attachment-grid">
                  {parseAttachments(thread.attachment).map((attachmentPath, index) => 
                    renderAttachment(attachmentPath, index)
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="thread-date">
            {t.postedOn} {formatDate(thread.created_at)}
          </div>

          <div className="vote-section">
            <button 
              className={`vote-button upvote ${userVotes.threadVote === 'upvote' ? 'active' : ''}`}
              onClick={() => handleVote('thread', 'upvote', thread.id)}
              disabled={votingInProgress[`thread-${thread.id}`]}
              title={t.upvote}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"/>
              </svg>
              <span>{thread.upvotes || 0}</span>
            </button>
            <button 
              className={`vote-button downvote ${userVotes.threadVote === 'downvote' ? 'active' : ''}`}
              onClick={() => handleVote('thread', 'downvote', thread.id)}
              disabled={votingInProgress[`thread-${thread.id}`]}
              title={t.downvote}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3zm7-13h2.67A2.31 2.31 0 0 1 22 4v7a2.31 2.31 0 0 1-2.33 2H17"/>
              </svg>
              <span>{thread.downvotes || 0}</span>
            </button>
          </div>
        </div>

        <div className="comments-section">
          <h2 className="comments-title">{t.replies} ({comments.length})</h2>

          <div className="comments-list">
            {comments.length === 0 ? (
              <div className="no-comments">{t.noReplies}</div>
            ) : (
              comments.map(comment => (
                <div 
                  key={comment.id} 
                  className={`comment-card ${comment.is_developer === 1 ? 'developer-comment' : ''}`}
                >
                  {comment.is_developer === 1 && (
                    <div className="developer-responder">
                      {comment.responder_display_name || comment.responder_name || '馆长'}
                    </div>
                  )}
                  
                  <div className="comment-content">{comment.content}</div>
                  
                  {comment.attachment && parseAttachments(comment.attachment).length > 0 && (
                    <div className="comment-attachment">
                      {parseAttachments(comment.attachment).map((attachmentPath, index) => 
                        renderAttachment(attachmentPath, index)
                      )}
                    </div>
                  )}
                  
                  <div className="comment-footer">
                    <div className="comment-date">
                      {formatDate(comment.created_at)}
                      {comment.edited_at && <span className="comment-edited"> · {t.edited}</span>}
                    </div>
                    <div className="comment-vote-section">
                      <button 
                        className={`vote-button upvote ${userVotes.commentVotes[comment.id] === 'upvote' ? 'active' : ''}`}
                        onClick={() => handleVote('comment', 'upvote', comment.id)}
                        disabled={votingInProgress[`comment-${comment.id}`]}
                        title={t.upvote}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"/>
                        </svg>
                        <span>{comment.upvotes || 0}</span>
                      </button>
                      <button 
                        className={`vote-button downvote ${userVotes.commentVotes[comment.id] === 'downvote' ? 'active' : ''}`}
                        onClick={() => handleVote('comment', 'downvote', comment.id)}
                        disabled={votingInProgress[`comment-${comment.id}`]}
                        title={t.downvote}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3zm7-13h2.67A2.31 2.31 0 0 1 22 4v7a2.31 2.31 0 0 1-2.33 2H17"/>
                        </svg>
                        <span>{comment.downvotes || 0}</span>
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          <form className="comment-form" onSubmit={handleCommentSubmit}>
            <h3 className="form-title">{t.addReply}</h3>
            
            <textarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder={t.replyPlaceholder}
              rows="4"
              required
            />

            <div className="form-file-group">
              <label>{t.attachmentOptional}</label>
              <div className="image-upload-container">
                {commentImages.map((image, index) => (
                  <div key={index} className="image-preview">
                    <img src={URL.createObjectURL(image)} alt={`预览 ${index + 1}`} />
                    <button 
                      type="button" 
                      className="remove-image-btn"
                      onClick={() => setCommentImages(prev => prev.filter((_, i) => i !== index))}
                    >
                      ×
                    </button>
                  </div>
                ))}
                
                {commentImages.length < 3 && (
                  <label className="image-upload-box">
                    <input 
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={(e) => {
                        const files = Array.from(e.target.files);
                        const validImages = files.filter(file => {
                          // Check file type
                          if (!file.type.startsWith('image/')) {
                            alert(`${file.name} ${lang === 'zh' ? '不是图片文件' : 'is not an image file'}`);
                            return false;
                          }
                          // Check file size (5MB)
                          if (file.size > 5 * 1024 * 1024) {
                            alert(`${file.name} ${lang === 'zh' ? '超过5MB' : 'exceeds 5MB'}`);
                            return false;
                          }
                          return true;
                        });
                        
                        // Add images up to limit of 3
                        const remainingSlots = 3 - commentImages.length;
                        const imagesToAdd = validImages.slice(0, remainingSlots);
                        setCommentImages(prev => [...prev, ...imagesToAdd]);
                        e.target.value = '';
                      }}
                      style={{ display: 'none' }}
                    />
                    <div className="upload-placeholder">
                      <span className="upload-icon">+</span>
                      <span className="upload-text">{t.addImage}</span>
                    </div>
                  </label>
                )}
              </div>
              <small className="form-hint">{t.imageHint}</small>
            </div>

            <button type="submit" className="submit-comment-button" disabled={submitting}>
              {submitting ? t.submitting : t.submitReply}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default FeedbackThread;
